import React from 'react';
import { Button } from '@/components/ui/button';
import { Check, Minus, Sparkles, Loader2 } from 'lucide-react';
import TierBadge from './TierBadge';

const ORDER = ['bronze', 'silver', 'gold', 'platinum'];
const SELLABLE = ['silver', 'gold', 'platinum'];
const Name = (tier) => tier.charAt(0).toUpperCase() + tier.slice(1);
const money = (value) => `$${Number(value || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Modules and Forum are levels, held in their own fields: a higher tier replaces the lower tier's
// amount instead of adding a row. Everything else is a plain yes/no line, inherited upward.
const LEVELS = [
  { key: 'Modules', field: 'modules_label' },
  { key: 'Forum', field: 'forum_label' },
];

/** Every tier keeps what the tiers below it hold, so each column is built up cumulatively. */
const buildTable = (tiers) => {
  const rows = [];
  const seen = new Set();
  const held = {};
  let running = new Set();

  ORDER.forEach((tier) => {
    const own = (tiers?.[tier]?.features || []).filter(Boolean);
    const ticks = new Set(running);
    own.forEach((line) => {
      ticks.add(line);
      if (!seen.has(line)) {
        seen.add(line);
        rows.push(line);
      }
    });
    held[tier] = { ticks, own };
    running = ticks;
  });

  return { rows, held };
};

/** People buy on the difference: what this tier changes or adds over the one below it. */
const addedBy = (tiers, held, tier) => {
  const below = ORDER[ORDER.indexOf(tier) - 1];
  const levels = LEVELS
    .filter(({ field }) => !below || tiers[tier]?.[field] !== tiers[below]?.[field])
    .map(({ key, field }) => (key === 'Modules' ? tiers[tier][field] : `${key}: ${tiers[tier][field]}`));
  return [...levels, ...held[tier].own];
};

export const TierComparison = ({ copy, prices, currentTier, suggestedTier, onChoose, loading }) => {
  if (loading || !copy?.tiers) {
    return (
      <div className="py-12 text-center text-gray-500" data-testid="tier-comparison-loading">
        <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" /> Gathering the tiers…
      </div>
    );
  }

  const { tiers, badge, badge_label: badgeLabel } = copy;
  const { rows, held } = buildTable(tiers);

  return (
    <div className="space-y-5" data-testid="tier-comparison">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        {ORDER.map((tier) => {
          const isCurrent = tier === currentTier;
          const isSuggested = tier === suggestedTier && !isCurrent;
          const price = prices?.tiers?.[tier];
          const adds = addedBy(tiers, held, tier);
          return (
            <div
              key={tier}
              className={`rounded-xl border-2 p-4 flex flex-col ${
                isSuggested ? 'border-cyan-500 bg-cyan-50/60'
                  : isCurrent ? 'border-gray-400 bg-gray-50'
                    : 'border-gray-200'}`}
              data-testid={`tier-column-${tier}`}
            >
              <div className="flex items-center justify-between gap-2 mb-1">
                <div className="flex items-center gap-2">
                  <TierBadge tier={tier} size="sm" />
                  <span className="font-semibold">{Name(tier)}</span>
                </div>
                {isCurrent && (
                  <span className="text-xs font-semibold text-gray-600 bg-gray-200 rounded-full px-2 py-0.5 whitespace-nowrap"
                        data-testid={`tier-youre-here-${tier}`}>
                    You're here
                  </span>
                )}
              </div>
              <div className="flex items-center justify-between gap-2">
                <p className="text-xs text-gray-500">{tiers[tier].modules_label}</p>
                {badge === tier && !isCurrent && (
                  <span className="text-[11px] font-semibold text-amber-900 bg-amber-100 border border-amber-300 rounded-full px-2 py-0.5 inline-flex items-center gap-1 whitespace-nowrap"
                        data-testid={`tier-badge-flag-${tier}`}>
                    <Sparkles className="w-3 h-3 flex-shrink-0" /> {badgeLabel}
                  </span>
                )}
              </div>

              {tier === 'bronze' ? (
                <p className="text-2xl font-bold text-gray-500 mt-2">Free</p>
              ) : (
                <div className="mt-2">
                  <p className="text-2xl font-bold text-cyan-700" data-testid={`tier-price-${tier}`}>
                    {money(price?.pay_in_full)}
                  </p>
                  <p className="text-xs text-gray-500">
                    or {price?.plan_count} × {money(price?.payments?.[0]?.billed)} a month
                  </p>
                </div>
              )}

              {adds.length > 0 && (
                <div className="mt-3 rounded-lg bg-white/70 border border-gray-200 p-2.5"
                     data-testid={`tier-adds-${tier}`}>
                  <p className="text-xs font-semibold text-gray-700 mb-1">
                    {tier === 'bronze' ? 'What Bronze includes' : `What ${Name(tier)} adds`}
                  </p>
                  <ul className="space-y-1">
                    {adds.map((line) => (
                      <li key={line} className="text-xs text-gray-700 flex items-start gap-1.5">
                        <Check className="w-3.5 h-3.5 text-green-600 mt-0.5 flex-shrink-0" />
                        <span>{line}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {tier !== 'bronze' && (
                <p className="text-xs text-gray-500 mt-2">
                  …and everything in {Name(ORDER[ORDER.indexOf(tier) - 1])}
                </p>
              )}

              <div className="mt-auto pt-3">
                {tier === 'bronze' || isCurrent ? (
                  <p className="text-xs text-gray-500">
                    {isCurrent ? 'Your membership today' : 'Where everyone starts'}
                  </p>
                ) : (
                  <Button
                    onClick={() => onChoose(tier)}
                    className="w-full"
                    data-testid={`choose-tier-${tier}`}
                  >
                    Choose {Name(tier)}
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <details className="rounded-xl border border-gray-200" data-testid="tier-full-grid">
        <summary className="cursor-pointer px-4 py-3 text-sm font-semibold text-gray-700">
          See every feature side by side
        </summary>
        <div className="overflow-x-auto px-2 pb-3">
          <table className="w-full text-sm">
            <thead>
              <tr>
                <th className="text-left font-semibold text-gray-600 py-2 px-2">Feature</th>
                {ORDER.map((tier) => (
                  <th key={tier} className="font-semibold text-gray-600 py-2 px-2 text-center">
                    {Name(tier)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {LEVELS.map(({ key, field }) => (
                <tr key={key} className="border-t border-gray-100">
                  <td className="py-2 px-2 text-gray-800">{key}</td>
                  {ORDER.map((tier) => (
                    <td key={tier} className="py-2 px-2 text-center text-gray-800"
                        data-testid={`cell-${tier}-${key}`}>
                      {tiers[tier]?.[field] || <Minus className="w-4 h-4 text-gray-300 mx-auto" />}
                    </td>
                  ))}
                </tr>
              ))}
              {rows.map((line) => (
                <tr key={line} className="border-t border-gray-100">
                  <td className="py-2 px-2 text-gray-800">{line}</td>
                  {ORDER.map((tier) => (
                    <td key={tier} className="py-2 px-2 text-center" data-testid={`cell-${tier}-${line}`}>
                      {held[tier].ticks.has(line)
                        ? <Check className="w-4 h-4 text-green-600 mx-auto" />
                        : <Minus className="w-4 h-4 text-gray-300 mx-auto" />}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>

      <p className="text-xs text-gray-500">
        Each tier includes everything in the tier below it. Upgrading never resets your maintenance
        anniversary, and what you've already paid comes off the price as credit.
      </p>
    </div>
  );
};

export default TierComparison;
export { SELLABLE };

/** Everything a tier holds, including what it inherits — for screens that show one tier alone. */
export const cumulativeFeatures = (tiers, tier) => {
  const { rows, held } = buildTable(tiers || {});
  if (!held[tier]) return [];
  return [
    tiers[tier].modules_label,
    `Forum: ${tiers[tier].forum_label}`,
    ...rows.filter((line) => held[tier].ticks.has(line)),
  ].filter(Boolean);
};
